import React, { useEffect, useState, useMemo, useRef, useReducer } from 'react'
import { useSelector, useDispatch, TypedUseSelectorHook } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { throttle } from 'lodash'
import qs from 'qs'

import { getFindUserResoureceFn, getBizArrFromUserBiz } from './util'
import {
  resourceMap,
  ResourceMap,
  ResourceMapKeys,
  businessLineMap,
  businessLineTextMap,
} from './consts'
import type { RootState, AppDispatch } from '../redux/store'
import request from './request'

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const useRefBounding = <T extends React.MutableRefObject<HTMLElement | null>>(eleRef: T) => {
  const [bounding, setBounding] = useState<DOMRect>({} as DOMRect)
  useEffect(() => {
    const start = () => {
      if (eleRef.current) {
        const bounding = eleRef.current.getBoundingClientRect()
        setBounding(bounding)
      }
    }
    start()
    const throttleStart = throttle(start, 100)
    window.addEventListener('resize', throttleStart)
    return () => {
      window.removeEventListener('resize', throttleStart)
    }
  }, [eleRef])
  return bounding
}

/**
 * UseTimingRequest 定时任务
 * @param fn 定时任务方法
 * @param param useEffect依赖，此依赖发生变更，定时任务自动更新
 * @param time 定时任务时间
 */
type UseTimingRequest = (
  fn: ((param: any) => () => void) | ((param: any) => void),
  param: any[]
) => void
export const useTimingRequest: UseTimingRequest = (fn, param, time = 300000) => {
  useEffect(() => {
    let cleanFn: any = null
    if (fn) {
      cleanFn = fn(param)
    }
    const timer = setInterval(() => {
      if (fn) {
        cleanFn = fn(param)
      }
    }, time)
    return () => {
      cleanFn && typeof cleanFn === 'function' && cleanFn()
      clearInterval(timer)
    }
  }, param)
}

export const useValidateResource = (userResources: any[]) =>
  useMemo(() => {
    return getFindUserResoureceFn(userResources)
  }, [userResources])

export const useUserResources = <TSelected>(
  cb: (resourceMap: ResourceMap) => TSelected,
  resources?: UserResources
): TSelected => {
  const state = useSelector((state: RootState) => state)
  let userResources = state?.app?.userResources ?? []
  if (resources) {
    userResources = resources
  }
  return useMemo(() => {
    const modulekeys = Object.keys(resourceMap) as ResourceMapKeys[]
    const findUserResourece = getFindUserResoureceFn(userResources)
    const result = {} as ResourceMap
    modulekeys.forEach(modulekey => {
      const obj = resourceMap[modulekey]
      result[modulekey] = Object.keys(obj).reduce((prev: any, key) => {
        prev[key] = findUserResourece(obj[key as keyof typeof obj])
        return prev
      }, {} as Record<keyof typeof obj, ReturnType<typeof findUserResourece>>)
    })
    return cb(result)
  }, [userResources])
}

type UseCallBackState<T> = (
  oldData: T
) => [data: T, cb: (newData: T, cb: (data: T) => void) => void]

export const useCallBackState: UseCallBackState<unknown> = oldData => {
  const cbRef = useRef<any>()
  const [data, setData] = useState(oldData)
  useEffect(() => {
    cbRef.current && cbRef.current(data)
  }, [data])
  return [
    data,
    (newData: any, cb: any) => {
      cbRef.current = cb
      setData(newData)
    },
  ]
}

export const useForceUpdate = () => {
  const forceUpdate = useReducer((bool: any) => !bool, true)[1]
  return forceUpdate
}

export const useDataCenterSearchParams = () => {
  return useMemo(() => {
    const searchStr = location.hash?.split('?')[1]
    if (searchStr) {
      try {
        const query = atob(decodeURIComponent(searchStr))
        return qs.parse(query)
      } catch (error) {
        return {}
      }
    }
    return {}
  }, [location.hash])
}

/**
 * 获取query
 */
export const useQuery = () => {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

/**
 *
 * @param bizLine 业务线
 * @returns 业务线下拉框选项 Options
 */
export const useBizOptions = (bizLine = businessLineMap.double) => {
  const bizOptions = useMemo(() => {
    return getBizArrFromUserBiz(bizLine, false).map(item => ({
      label: businessLineTextMap[item as keyof typeof businessLineTextMap],
      value: item,
    }))
  }, [bizLine])
  return bizOptions
}

/**
 * 防抖搜索自定义hook
 * @returns {SearchResult} 包含查询列表的方法
 */
interface SearchResult {
  queryList: (value: string, callback: (data: any[]) => void, formatParamsFn?: any) => void
}
export const useSearch = (api: string): SearchResult => {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let currentValue = ''

  /**
   * @param {string} value - 搜索关键词
   * @param {Function} callback - 回调函数，用于处理搜索结果
   */
  const queryList = (
    value: string,
    callback: (data: any[]) => void,
    formatParamsFn?: any
  ): void => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    currentValue = value

    const fake = () => {
      const params = formatParamsFn ? formatParamsFn(value) : { keyword: value }
      request(api, { data: params }).then((res: any) => {
        if (currentValue === value && res?.code === 0) {
          callback(res?.data)
        }
      })
    }

    if (value) {
      timeout = setTimeout(fake, 300)
    } else {
      callback([])
    }
  }

  return {
    queryList,
  }
}
